import { useEffect, useRef, useState } from 'react';

import { erzeugeBildLeser, quittiere } from '../kamera-scan';
import { normalisiereEan } from '../lager';

/**
 * Kamera als Scanner (Handy, Tablet): Rückkamera, Strichcode in den Rahmen
 * halten. Jeder gültige Code wird gemeldet; derselbe Code erst wieder nach
 * 1,5 Sekunden, damit ein ruhig gehaltener Artikel nicht mehrfach zählt.
 */
export function KameraScanner(props: { onCode: (ean: string) => void; onSchliessen: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const leinwand = useRef<HTMLCanvasElement>(null);
  const meldung = useRef(props.onCode);
  meldung.current = props.onCode;
  const [fehler, setFehler] = useState<string | null>(null);
  const [zuletzt, setZuletzt] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    let strom: MediaStream | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let letzter = { code: '', zeit: 0 };

    async function start(): Promise<void> {
      // Ohne HTTPS (oder in alten Browsern) fehlt mediaDevices zur Laufzeit trotz Typangabe.
      if (!('mediaDevices' in navigator)) {
        setFehler('Kamera nur über HTTPS verfügbar');
        return;
      }
      try {
        strom = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        });
      } catch {
        setFehler('Kein Zugriff auf die Kamera – in den Browser-Einstellungen erlauben');
        return;
      }
      const v = video.current;
      if (!aktiv || v === null) {
        strom.getTracks().forEach((t) => {
          t.stop();
        });
        return;
      }
      v.srcObject = strom;
      await v.play().catch(() => undefined);
      const leser = await erzeugeBildLeser();
      const runde = async (): Promise<void> => {
        if (!aktiv || video.current === null || leinwand.current === null) {
          return;
        }
        try {
          const roh = await leser.lies(video.current, leinwand.current);
          const ean = roh === null ? null : normalisiereEan(roh);
          const jetzt = Date.now();
          if (ean !== null && (ean !== letzter.code || jetzt - letzter.zeit > 1500)) {
            letzter = { code: ean, zeit: jetzt };
            quittiere();
            setZuletzt(ean);
            meldung.current(ean);
          }
        } catch {
          // einzelnes Bild nicht lesbar – nächstes versuchen
        }
        timer = setTimeout(() => void runde(), 200);
      };
      void runde();
    }
    void start();
    return () => {
      aktiv = false;
      if (timer !== null) {
        clearTimeout(timer);
      }
      strom?.getTracks().forEach((t) => {
        t.stop();
      });
    };
  }, []);

  return (
    <div className="zahlung-dialog">
      <div className="karte kamera-scanner">
        <h2>Mit der Kamera scannen</h2>
        <div className="kamera-bild">
          <video ref={video} playsInline muted />
          <div className="kamera-rahmen" aria-hidden="true" />
        </div>
        <canvas ref={leinwand} hidden />
        {fehler !== null ? (
          <p className="fehler">{fehler}</p>
        ) : (
          <p className="hinweis">
            {zuletzt === null ? 'Strichcode in den Rahmen halten' : `Erkannt: ${zuletzt}`}
          </p>
        )}
        <button type="button" className="taste" onClick={props.onSchliessen}>
          Fertig
        </button>
      </div>
    </div>
  );
}
